import { Check } from "lucide-react";

const BASE_DECLARATIONS = [
  "I confirm that the applicant will exit the UAE before visa expiry or complete a valid visa renewal.",
  "I accept full responsibility for any overstay fines, penalties, absconding charges, legal fees, or immigration violations.",
  "I understand that visa approval or rejection is solely at the discretion of UAE Immigration Authorities.",
  "I acknowledge that White Sky Travel and Tourism LLC has no control or influence over visa approvals or rejections.",
  "I confirm that all documents and information provided are genuine, accurate, and valid.",
  "I confirm that the applicant is fully aware of the visa application process, even if applied by a third party.",
  "I declare that there is no other pending or active visa application for the applicant in UAE immigration systems.",
  "I understand that visa fee is non-refundable.",
  "I consent to electronic records and digital signatures as legally binding.",
];

const VISA_RENEWAL_DECLARATION = "In case of visa renewal via airport or bus, I agree to bear the full deportation ticket cost if the visa is rejected or denied.";

const PDFPreview = ({ data }) => {
  const { passenger, guarantor, visaType, declarations } = data;
  
  // Check if visa type is renewal-related
  const isRenewalType = visaType?.toLowerCase().includes("renewal") || 
    visaType?.toLowerCase().includes("airport") || 
    visaType?.toLowerCase().includes("bus");
  
  const displayDeclarations = isRenewalType 
    ? [...BASE_DECLARATIONS.slice(0, 2), VISA_RENEWAL_DECLARATION, ...BASE_DECLARATIONS.slice(2)]
    : BASE_DECLARATIONS;

  return (
    <div className="pdf-preview" data-testid="pdf-preview">
      {/* Header */}
      <div className="text-center border-b border-slate-200 pb-6 mb-6">
        <h1 className="text-xl font-bold text-slate-900 mb-1">
          WHITE SKY TRAVEL AND TOURISM LLC
        </h1>
        <h2 className="text-base text-slate-500">
          GUARANTEE LETTER / LIABILITY CONFIRMATION
        </h2>
        <p className="text-sm text-slate-400 mt-2 font-mono">
          REF: WST-{new Date().toISOString().slice(0, 10).replace(/-/g, "")}-XXXXXX
        </p>
      </div>

      {/* Applicant Details */}
      <div className="mb-6">
        <h3 className="section-title">APPLICANT DETAILS</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="text-slate-500">Full Name:</div>
          <div className="text-slate-900 font-medium">{passenger?.full_name || "—"}</div>
          <div className="text-slate-500">Passport No:</div>
          <div className="text-slate-900 font-medium font-mono">{passenger?.passport_number || "—"}</div>
          <div className="text-slate-500">Nationality:</div>
          <div className="text-slate-900 font-medium">{passenger?.nationality || "—"}</div>
          <div className="text-slate-500">Contact:</div>
          <div className="text-slate-900 font-medium">{passenger?.contact_number || "—"}</div>
          <div className="text-slate-500">Email:</div>
          <div className="text-slate-900 font-medium">{passenger?.email || "—"}</div>
          <div className="text-slate-500">Relation to Guarantor:</div>
          <div className="text-slate-900 font-medium capitalize">
            {passenger?.relation_to_guarantor === "other" && passenger?.other_relation 
              ? passenger.other_relation 
              : (passenger?.relation_to_guarantor || "—")}
          </div>
        </div>
      </div>

      {/* Guarantor Details */}
      <div className="mb-6">
        <h3 className="section-title">GUARANTOR DETAILS</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="text-slate-500">Full Name:</div>
          <div className="text-slate-900 font-medium">{guarantor?.full_name || "—"}</div>
          <div className="text-slate-500">
            {guarantor?.id_type === "emirates_id" ? "Emirates ID:" : "Passport:"}
          </div>
          <div className="text-slate-900 font-medium font-mono">{guarantor?.id_number || "—"}</div>
          <div className="text-slate-500">Nationality:</div>
          <div className="text-slate-900 font-medium">{guarantor?.nationality || "—"}</div>
          <div className="text-slate-500">Contact:</div>
          <div className="text-slate-900 font-medium">{guarantor?.contact_number || "—"}</div>
          <div className="text-slate-500">Email:</div>
          <div className="text-slate-900 font-medium">{guarantor?.email || "—"}</div>
        </div>
      </div>

      {/* Visa Type */}
      <div className="mb-6">
        <h3 className="section-title">VISA TYPE</h3>
        <p className="text-sm text-slate-900 font-medium">{visaType || "Not selected"}</p>
      </div>

      {/* Declarations */}
      <div className="mb-6">
        <h3 className="section-title">DECLARATIONS</h3>
        <div className="space-y-2">
          {displayDeclarations.map((declaration, index) => (
            <div key={index} className="flex items-start gap-2 text-xs">
              <span className={declarations?.[index] ? "text-emerald-600" : "text-slate-300"}>
                {declarations?.[index] ? "☑" : "☐"}
              </span>
              <span className={declarations?.[index] ? "text-slate-700" : "text-slate-400"}>
                {declaration}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Liability Clause */}
      <div className="mb-6 bg-slate-50 p-4 rounded-sm border border-slate-200">
        <h3 className="text-xs font-semibold text-slate-900 mb-2">LIABILITY & INDEMNITY</h3>
        <p className="text-xs text-slate-600 leading-relaxed">
          The undersigned acknowledges and agrees that all liability for any immigration-related penalties, fines, overstay charges, absconding fees, visa auto-renewal charges, or other legal consequences shall rest solely with the applicant and/or guarantor. In the event that any such charges arise, the applicant and/or guarantor shall be fully responsible and legally bound to pay the applicable amount to White Sky Travel and Tourism LLC.
        </p>
      </div>

      {/* Signature Placeholder */}
      <div className="border-t border-slate-200 pt-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Digital Signature</p>
            <div className="w-32 h-12 border border-dashed border-slate-300 rounded-sm flex items-center justify-center mt-1">
              <span className="text-xs text-slate-400">Signature</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Date</p>
            <p className="text-sm text-slate-900 font-medium mt-1">
              {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-slate-200 text-center">
        <p className="text-xs text-slate-400">
          White Sky Travel and Tourism LLC • Room 202 Royal Class Building, DIP 1, Dubai
        </p>
        <p className="text-xs text-slate-400">
          +971 4 220 2133 | +971 52 292 1011
        </p>
      </div>
    </div>
  );
};

export default PDFPreview;
